import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { buscarProductos, unicoProducto } from '../../utils/buscarProductos';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Plus, Minus, Trash2, User, CreditCard, CheckCircle, X, Package, Banknote, ArrowRightLeft, Smartphone, Archive, RefreshCw, AlertTriangle } from 'lucide-react';
import VistaComprobante from '../../components/VistaComprobante';
import { calcularDigitoVerificador, TIPOS_DOCUMENTO } from '../../utils/ruc';
import { mensajeDeError } from '../../utils/errores';

const FORMAS_PAGO = [
    { key: 'efectivo',      label: 'Efectivo',      icon: <Banknote size={14} /> },
    { key: 'transferencia', label: 'Transfer.',     icon: <ArrowRightLeft size={14} /> },
    { key: 'tarjeta',       label: 'Tarjeta',       icon: <CreditCard size={14} /> },
    { key: 'qr',            label: 'QR',            icon: <Smartphone size={14} /> },
];

const BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

export default function Carrito() {
    const [productos, setProductos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [settings, setSettings] = useState(null);
    const [searchProd, setSearchProd] = useState('');
    const [cart, setCart] = useState([]);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [clienteSearch, setClienteSearch] = useState('');
    const [clienteSuggestions, setClienteSuggestions] = useState([]);
    // Alta rápida de cliente, sin salir del mostrador. El cliente está
    // esperando: mandarlo al módulo de Clientes significa perder el carrito o
    // abrir otra pestaña, y en la práctica termina en una venta sin cliente.
    const [altaCliente, setAltaCliente] = useState(null);
    const [guardandoCliente, setGuardandoCliente] = useState(false);
    const [errorCliente, setErrorCliente] = useState('');
    // Confirmación previa al registro. Antes la venta se registraba de una y el
    // comprobante salía con «CONTADO» fijo, sin que nadie lo hubiera elegido:
    // una venta a crédito quedaba documentada como si se hubiera cobrado.
    const [confirmacion, setConfirmacion] = useState(null);
    const [ventaExitosa, setVentaExitosa] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formaPago, setFormaPago] = useState('efectivo');
    const [datosPago, setDatosPago] = useState({});
    const [cajaAbierta, setCajaAbierta] = useState(null); // null=cargando, true/false
    // Un fallo al cargar la pantalla no es lo mismo que un catálogo vacío.
    const [errorCarga, setErrorCarga] = useState('');

    const searchRef = useRef(null);
    const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userRol = currentUser.rol || (currentUser.is_admin ? 'Administrador' : 'Sin rol');
    const necesitaTurno = userRol === 'Cajero' || userRol === 'Sin rol';
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Si el rol requiere turno, verificar primero
                if (necesitaTurno) {
                    const { data } = await axios.get(`${BASE}/caja/estado/`);
                    setCajaAbierta(data.estado === 'abierto');
                    if (data.estado !== 'abierto') return; // no cargar productos si no hay turno
                } else {
                    setCajaAbierta(true);
                }
                const [resProd, resCliente, resSettings] = await Promise.all([
                    axios.get(`${BASE}/productos/`),
                    axios.get(`${BASE}/clientes/`),
                    axios.get(`${BASE}/settings/`),
                ]);
                setProductos(resProd.data.filter(p => p.activo));
                setClientes(resCliente.data.filter(c => c.activo));
                setSettings(resSettings.data);
            } catch (err) {
                // Si falla la verificación de turno no se bloquea la venta.
                setCajaAbierta(true);
                // Pero el fallo se dice. Antes se tragaba cualquier error y la
                // pantalla quedaba mostrando «No se encontraron productos»:
                // afirmaba que el catálogo estaba vacío cuando lo que pasaba
                // era que no se pudo leer. Buscar el problema en los artículos
                // es buscarlo donde no está.
                setErrorCarga(mensajeDeError(
                    err, 'No se pudieron cargar los artículos. Revisá la conexión y recargá.'));
            }
        };
        fetchData();
        // Depende del rol: un cajero no debe ver productos sin turno abierto.
        // El valor sale de la sesion y no cambia mientras la pantalla vive, asi
        // que declararlo no agrega recargas.
    }, [necesitaTurno]);

    const handleClienteSearch = (val) => {
        setClienteSearch(val);
        if (val.length >= 2) {
            const filtered = clientes.filter(c =>
                c.nombre.toLowerCase().includes(val.toLowerCase()) ||
                (c.documento_completo && c.documento_completo.includes(val))
            );
            setClienteSuggestions(filtered.slice(0, 6));
        } else {
            setClienteSuggestions([]);
        }
    };

    const selectCliente = (c) => {
        setSelectedCliente(c);
        setClienteSearch(c.nombre);
        setClienteSuggestions([]);
    };

    // El nombre arranca con lo que ya se tipeó en la búsqueda: si escribió el
    // nombre y no apareció, no tiene por qué volver a escribirlo.
    const abrirAltaCliente = () => {
        setAltaCliente({
            nombre: clienteSearch.trim(),
            tipo_documento: 'ci',
            numero_documento: '',
            digito_verificador: '',
            telefono: '',
        });
        setErrorCliente('');
        setClienteSuggestions([]);
    };

    const guardarClienteNuevo = async (e) => {
        e.preventDefault();
        if (!altaCliente?.nombre.trim()) {
            setErrorCliente('Poné al menos el nombre.');
            return;
        }
        setGuardandoCliente(true);
        setErrorCliente('');
        try {
            const { data } = await axios.post(`${BASE}/clientes/`, {
                ...altaCliente,
                digito_verificador: altaCliente.tipo_documento === 'ruc'
                    ? altaCliente.digito_verificador : '',
                activo: true,
            });
            // Queda disponible para el resto de la jornada sin recargar la
            // pantalla, y seleccionado para esta venta.
            setClientes(prev => [...prev, data]);
            selectCliente(data);
            setAltaCliente(null);
        } catch (err) {
            setErrorCliente(mensajeDeError(err, 'No se pudo guardar el cliente.'));
        } finally {
            setGuardandoCliente(false);
        }
    };

    const prodFiltrados = buscarProductos(productos, searchProd);

    const handleScannerEnter = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const term = searchProd.trim();
        if (!term) return;
        const candidato = unicoProducto(productos, term);
        if (candidato && candidato.stock_actual > 0) {
            addToCart(candidato);
            setSearchProd('');
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    };

    const addToCart = (prod) => {
        setCart(prev => {
            const exists = prev.find(i => i.producto_id === prod.id);
            if (exists) {
                if (exists.cantidad >= prod.stock_actual) return prev;
                return prev.map(i => i.producto_id === prod.id
                    ? { ...i, cantidad: i.cantidad + 1, subtotal: (i.cantidad + 1) * i.precio_unitario }
                    : i);
            }
            return [...prev, {
                producto_id: prod.id, nombre: prod.nombre, codigo: prod.codigo_barras,
                precio_unitario: parseFloat(prod.precio_venta), costo_unitario: parseFloat(prod.costo_compra),
                // La tasa viaja con el ítem para poder mostrarla al confirmar.
                // Quien la aplica es el servidor, que la copia del artículo al
                // guardar: acá es solo para que el cajero vea qué va a salir
                // impreso antes de cobrar.
                tasa_iva: prod.tasa_iva,
                stock_max: prod.stock_actual, cantidad: 1, subtotal: parseFloat(prod.precio_venta)
            }];
        });
    };

    const updateQty = (prod_id, qty) => {
        if (qty <= 0) { removeFromCart(prod_id); return; }
        setCart(prev => prev.map(i => i.producto_id === prod_id
            ? { ...i, cantidad: Math.min(qty, i.stock_max), subtotal: Math.min(qty, i.stock_max) * i.precio_unitario }
            : i
        ));
    };

    const removeFromCart = (prod_id) => setCart(prev => prev.filter(i => i.producto_id !== prod_id));

    const total = cart.reduce((acc, i) => acc + i.subtotal, 0);

    const abrirConfirmacion = () => {
        if (cart.length === 0) { setError('El carrito está vacío.'); return; }
        setError('');
        setConfirmacion({ condicion_venta: 'contado', cuotas: 1, plazo_dias: 30 });
    };

    const handleConfirmarVenta = async () => {
        if (cart.length === 0) { setError('El carrito está vacío.'); return; }
        const aCredito = confirmacion?.condicion_venta === 'credito';

        // Una venta a crédito sin cliente no se puede cobrar después: no hay a
        // quién reclamarle. Y el comprobante electrónico la rechaza por lo
        // mismo, así que conviene detenerla acá y no cuando ya salió.
        if (aCredito && !selectedCliente) {
            setError('Una venta a crédito necesita un cliente identificado.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const payload = {
                cliente: selectedCliente?.id || null,
                estado: 'pagado',
                forma_pago: formaPago,
                datos_pago: datosPago,
                condicion_venta: confirmacion?.condicion_venta || 'contado',
                cuotas: aCredito ? Number(confirmacion.cuotas) || 0 : 0,
                plazo_dias: aCredito ? Number(confirmacion.plazo_dias) || 0 : 0,
                detalles: cart.map(i => ({
                    producto: i.producto_id,
                    cantidad: i.cantidad,
                    precio_unitario: i.precio_unitario,
                    costo_unitario: i.costo_unitario,
                    subtotal: i.subtotal
                }))
            };
            const res = await axios.post(`${BASE}/ventas/`, payload);
            setVentaExitosa(res.data);
            setConfirmacion(null);
            setCart([]);
            setSelectedCliente(null);
            setClienteSearch('');
            const resProd = await axios.get(`${BASE}/productos/`);
            setProductos(resProd.data.filter(p => p.activo));
        } catch (err) {
            const stockErr = err.response?.data?.stock;
            if (stockErr) {
                setError(Array.isArray(stockErr) ? stockErr.join(' | ') : stockErr);
            } else {
                setError(mensajeDeError(err, 'Error al procesar la venta.'));
            }
        } finally { setLoading(false); }
    };

    // Cargando verificación de turno
    if (cajaAbierta === null) return (
        <div className="cargando">
            <RefreshCw className="animate-spin text-brand-800" size={28} />
        </div>
    );

    // Sin turno abierto (solo para Cajero)
    if (necesitaTurno && cajaAbierta === false) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-6 text-center">
            <div className="w-20 h-20 rounded-2xl bg-alerta-500/10 border border-alerta-500/20 flex items-center justify-center">
                <Archive size={40} className="text-alerta-500" />
            </div>
            <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Turno no iniciado</h2>
                <p className="text-slate-500 dark:text-slate-400 max-w-sm">
                    Debés abrir tu turno de caja antes de poder facturar. Registrá el efectivo inicial y comenzá tu jornada.
                </p>
            </div>
            <button
                onClick={() => navigate('/system/caja')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-500 text-negro-950 font-bold text-sm transition-all shadow-lg shadow-brand-500/20"
            >
                <Archive size={16} /> Ir a Abrir Turno
            </button>
        </div>
    );

    return (
        <div className="h-full flex gap-6" onClick={e => {
            // Re-enfocar el buscador si el click no fue en un input/button/select
            const tag = e.target.tagName;
            if (!['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA'].includes(tag)) {
                searchRef.current?.focus();
            }
        }}>
            {/* Panel Izquierdo: Catálogo */}
            <div className="flex-1 flex flex-col space-y-4 min-w-0">
                <div className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl p-4 flex items-center gap-3">
                    <ShoppingCart size={22} className="text-brand-800 shrink-0" />
                    <div className="relative flex-1">
                        <Search size={16} className="icono-campo" />
                        <input
                            ref={searchRef}
                            autoFocus
                            type="text" placeholder="Buscar o escanear código de barras..."
                            value={searchProd}
                            onChange={e => setSearchProd(e.target.value)}
                            onKeyDown={handleScannerEnter}
                            className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-white/10">
                            <tr className="text-xs text-slate-500 dark:text-gray-500 uppercase tracking-wider">
                                <th className="px-4 py-3 text-left font-semibold">Producto</th>
                                <th className="px-4 py-3 text-left font-semibold hidden md:table-cell">Código</th>
                                <th className="px-4 py-3 text-left font-semibold hidden lg:table-cell">Categoría</th>
                                <th className="px-4 py-3 text-right font-semibold">Precio</th>
                                <th className="px-4 py-3 text-center font-semibold">Stock</th>
                                <th className="px-3 py-3 w-10"></th>
                            </tr>
                        </thead>
                        <tbody className="tabla-filas">
                            {prodFiltrados.map(p => {
                                const sinStock = p.stock_actual === 0;
                                const stockBajo = !sinStock && p.stock_actual <= p.stock_minimo;
                                const enCarrito = cart.find(i => i.producto_id === p.id);
                                return (
                                    <tr
                                        key={p.id}
                                        onClick={() => !sinStock && addToCart(p)}
                                        className={`group transition-colors ${
                                            sinStock
                                                ? 'opacity-40 cursor-not-allowed'
                                                : 'cursor-pointer hover:bg-brand-500/5 dark:hover:bg-brand-500/10'
                                        } ${enCarrito ? 'bg-brand-500/5 dark:bg-brand-500/10' : ''}`}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-slate-900 dark:text-white leading-tight">{p.nombre}</div>
                                            {p.marca && <div className="text-xs text-slate-400 dark:text-gray-600 mt-0.5">{p.marca}</div>}
                                        </td>
                                        <td className="px-4 py-3 hidden md:table-cell">
                                            <span className="font-mono text-xs text-slate-400 dark:text-gray-600">{p.codigo_barras}</span>
                                        </td>
                                        <td className="px-4 py-3 hidden lg:table-cell">
                                            <span className="text-xs text-slate-500 dark:text-gray-400">{p.categoria_nombre || '—'}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <span className="font-bold text-brand-800 dark:text-brand-400">Gs. {parseFloat(p.precio_venta).toLocaleString()}</span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                                                sinStock
                                                    ? 'bg-error-500/15 text-error-500'
                                                    : stockBajo
                                                        ? 'bg-alerta-500/15 text-alerta-500'
                                                        : 'bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-gray-400'
                                            }`}>
                                                {stockBajo && <AlertTriangle size={10} />}
                                                {p.stock_actual}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 text-center">
                                            {enCarrito ? (
                                                <span className="w-7 h-7 rounded-lg bg-brand-500 text-negro-950 text-xs font-bold flex items-center justify-center mx-auto">
                                                    {enCarrito.cantidad}
                                                </span>
                                            ) : (
                                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center mx-auto text-slate-300 dark:text-gray-600 ${!sinStock ? 'group-hover:bg-brand-500 group-hover:text-negro-950 transition-all' : ''}`}>
                                                    <Plus size={14} />
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {prodFiltrados.length === 0 && errorCarga && (
                                <tr><td colSpan="6" className="px-6 py-12 text-center">
                                    <AlertTriangle size={32} className="mx-auto mb-3 text-error-500" />
                                    <p className="text-error-600 dark:text-error-400 font-semibold">{errorCarga}</p>
                                </td></tr>
                            )}
                            {prodFiltrados.length === 0 && !errorCarga && (
                                <tr>
                                    <td colSpan={6} className="text-center py-16 text-slate-400 dark:text-gray-600">
                                        <Package size={36} className="mx-auto mb-2 opacity-30" />
                                        <p className="text-sm">No se encontraron productos.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Panel Derecho: Carrito */}
            <div className="w-80 xl:w-96 flex flex-col gap-4 shrink-0">
                {/* Selector de cliente */}
                <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
                    <label htmlFor="carrito-cliente" className="block text-xs font-semibold text-slate-500 dark:text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <User size={12} /> Cliente
                    </label>
                    <div className="relative">
                        <input id="carrito-cliente"
                            type="text"
                            placeholder="Consumidor final (opcional)..."
                            value={clienteSearch}
                            onChange={e => {
                                // Tipear suelta la selección anterior. Sin esto,
                                // elegido un cliente el buscador queda mudo y hay
                                // que limpiarlo con la X para buscar otro.
                                setSelectedCliente(null);
                                handleClienteSearch(e.target.value);
                            }}
                            className="w-full px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                        />
                        {clienteSearch.length >= 2 && !selectedCliente && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/20 rounded-xl shadow-2xl z-10 overflow-hidden">
                                {clienteSuggestions.map(c => (
                                    <button key={c.id} type="button" onClick={() => selectCliente(c)}
                                        className="w-full text-left px-4 py-2.5 text-sm text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors border-b border-slate-100 dark:border-white/5 last:border-0">
                                        <div className="font-medium">{c.nombre}</div>
                                        {c.documento_completo && <div className="text-xs text-slate-400 dark:text-gray-500">{c.tipo_documento === 'ruc' ? 'RUC' : 'CI'}: {c.documento_completo}</div>}
                                    </button>
                                ))}
                                {/* El alta se ofrece siempre que se haya buscado algo, no solo
                                    cuando no hay resultados: puede existir un "Juan Perez" y
                                    el que está en el mostrador ser otro. */}
                                <button type="button" onClick={abrirAltaCliente}
                                    className="w-full text-left px-4 py-2.5 text-sm font-semibold text-brand-800 hover:bg-brand-500/10 transition-colors flex items-center gap-2 border-t border-slate-100 dark:border-white/10">
                                    <Plus size={14} />
                                    {clienteSuggestions.length > 0
                                        ? 'Registrar un cliente nuevo'
                                        : `Registrar «${clienteSearch.trim()}»`}
                                </button>
                            </div>
                        )}
                    </div>
                    {selectedCliente && (
                        <div className="mt-2 flex items-center justify-between bg-brand-500/10 border border-brand-500/30 px-3 py-2 rounded-lg">
                            <span className="text-sm text-brand-800 font-medium">{selectedCliente.nombre}</span>
                            <button onClick={() => { setSelectedCliente(null); setClienteSearch(''); }} className="text-slate-400 dark:text-gray-500 hover:text-error-400">
                                <X size={14} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Items del carrito */}
                <div className="flex-1 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-slate-200 dark:border-white/10">
                        <h3 className="font-bold text-slate-900 dark:text-white text-sm flex items-center justify-between">
                            <span className="flex items-center gap-2"><ShoppingCart size={16} className="text-brand-800" /> Carrito</span>
                            <span className="text-slate-400 dark:text-gray-500 text-xs">{cart.length} ítem(s)</span>
                        </h3>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {cart.length === 0 && (
                            <div className="text-center py-8 text-slate-400 dark:text-gray-600">
                                <ShoppingCart size={36} className="mx-auto mb-2 opacity-30" />
                                <p className="text-sm">Haz clic en un producto para agregarlo</p>
                            </div>
                        )}
                        {cart.map(item => (
                            <div key={item.producto_id} className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl p-3">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1 mr-2">
                                        <p className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">{item.nombre}</p>
                                        <p className="text-xs text-slate-400 dark:text-gray-500">Gs. {item.precio_unitario.toLocaleString()} c/u</p>
                                    </div>
                                    <button onClick={() => removeFromCart(item.producto_id)} className="text-slate-400 dark:text-gray-600 hover:text-error-400 transition-colors shrink-0">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => updateQty(item.producto_id, item.cantidad - 1)}
                                            className="w-7 h-7 rounded-lg bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/20 text-slate-900 dark:text-white flex items-center justify-center transition-colors">
                                            <Minus size={12} />
                                        </button>
                                        <input
                                            type="number" min="1" max={item.stock_max}
                                            value={item.cantidad}
                                            onChange={e => updateQty(item.producto_id, parseInt(e.target.value) || 1)}
                                            className="w-12 text-center bg-slate-200 dark:bg-white/10 border border-slate-300 dark:border-white/20 rounded-lg text-slate-900 dark:text-white text-sm py-1 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        />
                                        <button onClick={() => updateQty(item.producto_id, item.cantidad + 1)}
                                            className="w-7 h-7 rounded-lg bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/20 text-slate-900 dark:text-white flex items-center justify-center transition-colors">
                                            <Plus size={12} />
                                        </button>
                                    </div>
                                    <span className="font-bold text-slate-900 dark:text-white text-sm">Gs. {item.subtotal.toLocaleString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="border-t border-slate-200 dark:border-white/10 flex flex-col shrink-0">
                        {/* Área scrollable: total + forma de pago + datos adicionales */}
                        <div className="overflow-y-auto p-4 space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-slate-500 dark:text-gray-400 font-medium">Total</span>
                                <span className="titulo-pagina">Gs. {total.toLocaleString()}</span>
                            </div>

                            {/* Forma de pago */}
                            <div>
                                <p className="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider mb-1.5">Forma de pago</p>
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-1.5">
                                    {FORMAS_PAGO.map(fp => (
                                        <button
                                            key={fp.key}
                                            type="button"
                                            onClick={() => { setFormaPago(fp.key); setDatosPago({}); }}
                                            className={`flex flex-col items-center gap-1 py-2 px-1 rounded-xl border text-xs font-bold transition-all ${
                                                formaPago === fp.key
                                                    ? 'bg-brand-500 border-brand-500 text-negro-950 shadow-md shadow-brand-500/30'
                                                    : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-gray-400 hover:border-brand-400 hover:text-brand-800'
                                            }`}
                                        >
                                            {fp.icon}
                                            {fp.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Datos adicionales según forma de pago */}
                            {formaPago !== 'efectivo' && (
                                <DatosPagoPanel
                                    formaPago={formaPago}
                                    datos={datosPago}
                                    onChange={setDatosPago}
                                />
                            )}

                            {error && <p className="text-error-400 text-xs text-center">{error}</p>}
                        </div>

                        {/* Botón siempre visible al fondo */}
                        <div className="px-4 pb-4 shrink-0">
                            <button
                                onClick={abrirConfirmacion}
                                disabled={cart.length === 0 || loading}
                                className="w-full bg-brand-500 hover:bg-brand-400 disabled:opacity-50 disabled:cursor-not-allowed text-negro-950 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-brand-500/30"
                            >
                                <CreditCard size={18} />
                                {loading ? 'Procesando...' : `Confirmar · ${FORMAS_PAGO.find(f => f.key === formaPago)?.label}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Venta Exitosa */}
            {/* Confirmación de la venta.
                Es el paso que faltaba: antes se registraba de una y el
                comprobante salía con «CONTADO» fijo, sin que nadie lo hubiera
                elegido. Una venta a crédito quedaba documentada como si se
                hubiera cobrado, y eso no se arregla después: el comprobante ya
                está entregado. */}
            {confirmacion && (
                <div className="fondo-modal" onClick={() => !loading && setConfirmacion(null)}>
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl w-full max-w-lg shadow-2xl"
                        onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-white/10">
                            <h3 className="titulo-modal flex items-center gap-2">
                                <CreditCard size={18} className="text-brand-800" /> Confirmar venta
                            </h3>
                            <button type="button" disabled={loading} onClick={() => setConfirmacion(null)} className="boton-icono">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                            {error && <div className="aviso-error">{error}</div>}

                            {/* Qué se está vendiendo, para revisarlo antes de cobrar. */}
                            <div className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden overflow-x-auto">
                                <table className="w-full text-sm min-w-[22rem]">
                                    <thead className="tabla-encabezado">
                                        <tr>
                                            <th className="text-left px-3 py-2">Artículo</th>
                                            <th className="text-center px-2 py-2 w-16">Cant.</th>
                                            <th className="text-center px-2 py-2 w-16">IVA</th>
                                            <th className="text-right px-3 py-2 w-28">Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tabla-filas">
                                        {cart.map(i => (
                                            <tr key={i.producto_id}>
                                                <td className="px-3 py-2 text-slate-900 dark:text-white">{i.nombre}</td>
                                                <td className="px-2 py-2 text-center text-slate-600 dark:text-gray-300">{i.cantidad}</td>
                                                <td className="px-2 py-2 text-center text-slate-500 dark:text-gray-400">
                                                    {i.tasa_iva === 0 ? 'Ex.' : `${i.tasa_iva ?? 10}%`}
                                                </td>
                                                <td className="px-3 py-2 text-right font-semibold text-slate-900 dark:text-white">
                                                    {settings?.moneda || 'Gs.'} {Math.round(i.subtotal).toLocaleString('es-PY')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center justify-between px-1">
                                <span className="text-sm text-slate-500 dark:text-gray-400">
                                    {selectedCliente ? selectedCliente.nombre : 'Consumidor final'}
                                </span>
                                <span className="text-xl font-bold text-slate-900 dark:text-white">
                                    {settings?.moneda || 'Gs.'} {total.toLocaleString('es-PY')}
                                </span>
                            </div>

                            {/* Condición de venta */}
                            <div>
                                <p className="etiqueta-campo normal-case text-sm mb-2">Condición de venta</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {[
                                        { valor: 'contado', etiqueta: 'Contado', ayuda: 'Se cobra ahora' },
                                        { valor: 'credito', etiqueta: 'Crédito', ayuda: 'Se cobra después' },
                                    ].map(opcion => (
                                        <button key={opcion.valor} type="button"
                                            onClick={() => setConfirmacion({ ...confirmacion, condicion_venta: opcion.valor })}
                                            className={`px-4 py-3 rounded-xl border text-left transition-all ${
                                                confirmacion.condicion_venta === opcion.valor
                                                    ? 'bg-brand-500 border-brand-500 text-negro-950'
                                                    : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-gray-300 hover:border-brand-500/50'
                                            }`}>
                                            <span className="block font-bold text-sm">{opcion.etiqueta}</span>
                                            <span className="block text-xs opacity-80">{opcion.ayuda}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {confirmacion.condicion_venta === 'credito' && (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label htmlFor="venta-cuotas" className="etiqueta-campo normal-case text-sm">Cuotas</label>
                                            <input id="venta-cuotas" type="number" min="1" max="99" className="campo"
                                                value={confirmacion.cuotas}
                                                onChange={e => setConfirmacion({ ...confirmacion, cuotas: e.target.value })} />
                                        </div>
                                        <div>
                                            <label htmlFor="venta-plazo" className="etiqueta-campo normal-case text-sm">Plazo (días)</label>
                                            <input id="venta-plazo" type="number" min="1" max="365" className="campo"
                                                value={confirmacion.plazo_dias}
                                                onChange={e => setConfirmacion({ ...confirmacion, plazo_dias: e.target.value })} />
                                        </div>
                                    </div>
                                    {!selectedCliente && (
                                        <div className="aviso">
                                            Una venta a crédito necesita un cliente identificado: si nadie
                                            figura como deudor, no hay a quién cobrarle después.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="pie-modal">
                            <button type="button" disabled={loading} onClick={() => setConfirmacion(null)}
                                className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                Volver
                            </button>
                            <button type="button" onClick={handleConfirmarVenta}
                                disabled={loading || (confirmacion.condicion_venta === 'credito' && !selectedCliente)}
                                className="boton-principal disabled:opacity-50">
                                {loading ? 'Registrando...' : 'Registrar venta'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alta rápida de cliente.
                Pide lo mínimo —nombre, documento, teléfono— y nada más. Todo lo
                demás se completa después desde Clientes: en el mostrador, cada
                campo de más es una razón para saltearlo y terminar vendiendo
                sin cliente. */}
            {altaCliente && (
                <div className="fondo-modal" onClick={() => setAltaCliente(null)}>
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl w-full max-w-md shadow-2xl"
                        onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-white/10">
                            <h3 className="titulo-modal flex items-center gap-2">
                                <User size={18} className="text-brand-800" /> Cliente nuevo
                            </h3>
                            <button type="button" onClick={() => setAltaCliente(null)} className="boton-icono">
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={guardarClienteNuevo} className="p-5 space-y-4">
                            {errorCliente && (
                                <div className="aviso-error">{errorCliente}</div>
                            )}

                            <div>
                                <label htmlFor="pos-cliente-nombre" className="etiqueta-campo normal-case text-sm">
                                    Nombre *
                                </label>
                                <input id="pos-cliente-nombre" autoFocus required type="text" className="campo"
                                    placeholder="Juan Pérez"
                                    value={altaCliente.nombre}
                                    onChange={e => setAltaCliente({ ...altaCliente, nombre: e.target.value })} />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="pos-cliente-tipo" className="etiqueta-campo normal-case text-sm">
                                        Tipo de documento
                                    </label>
                                    <select id="pos-cliente-tipo" className="campo"
                                        value={altaCliente.tipo_documento}
                                        onChange={e => {
                                            const tipo = e.target.value;
                                            setAltaCliente({
                                                ...altaCliente,
                                                tipo_documento: tipo,
                                                digito_verificador: tipo === 'ruc'
                                                    ? calcularDigitoVerificador(altaCliente.numero_documento)
                                                    : '',
                                            });
                                        }}>
                                        {TIPOS_DOCUMENTO.map(t => (
                                            <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="pos-cliente-telefono" className="etiqueta-campo normal-case text-sm">
                                        Teléfono
                                    </label>
                                    <input id="pos-cliente-telefono" type="tel" className="campo"
                                        placeholder="0981 123 456"
                                        value={altaCliente.telefono}
                                        onChange={e => setAltaCliente({ ...altaCliente, telefono: e.target.value })} />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <label htmlFor="pos-cliente-numero" className="etiqueta-campo normal-case text-sm">
                                        Número
                                    </label>
                                    <input id="pos-cliente-numero" type="text" className="campo font-mono"
                                        placeholder="80012345"
                                        value={altaCliente.numero_documento}
                                        onChange={e => {
                                            const numero = e.target.value;
                                            // El dígito se completa solo: es un dato derivado,
                                            // no algo que el cajero tenga que calcular.
                                            setAltaCliente({
                                                ...altaCliente,
                                                numero_documento: numero,
                                                digito_verificador: altaCliente.tipo_documento === 'ruc'
                                                    ? calcularDigitoVerificador(numero)
                                                    : '',
                                            });
                                        }} />
                                </div>
                                <div>
                                    <label htmlFor="pos-cliente-digito" className="etiqueta-campo normal-case text-sm">
                                        Dígito
                                    </label>
                                    <input id="pos-cliente-digito" type="text" maxLength={1}
                                        className="campo font-mono text-center disabled:opacity-40"
                                        disabled={altaCliente.tipo_documento !== 'ruc'}
                                        value={altaCliente.digito_verificador}
                                        onChange={e => setAltaCliente({
                                            ...altaCliente,
                                            digito_verificador: e.target.value.replace(/\D/g, ''),
                                        })} />
                                </div>
                            </div>

                            <p className="text-xs text-slate-500 dark:text-gray-400">
                                El resto de los datos se completan después desde Clientes.
                            </p>

                            <div className="pie-modal">
                                <button type="button" onClick={() => setAltaCliente(null)}
                                    className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" disabled={guardandoCliente} className="boton-principal">
                                    {guardandoCliente ? 'Guardando...' : 'Guardar y usar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {ventaExitosa && (
                <div className="fondo-modal">
                    <div className="bg-white dark:bg-slate-900 border border-exito-500/30 rounded-3xl w-full max-w-sm shadow-2xl max-h-[92vh] overflow-y-auto">
                        <div className="text-center px-6 pt-6 pb-4">
                            <div className="w-12 h-12 bg-exito-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                <CheckCircle size={26} className="text-exito-400" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                {ventaExitosa.factura ? 'Factura emitida' : 'Venta registrada'}
                            </h2>
                            {ventaExitosa.factura ? (
                                <p className="text-sm text-slate-500 dark:text-gray-400 mt-1 font-mono">
                                    {ventaExitosa.factura.numero_completo}
                                </p>
                            ) : (
                                <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
                                    Venta #{ventaExitosa.id}
                                </p>
                            )}
                            <p className="text-2xl font-bold text-exito-400 mt-2">
                                {settings?.moneda || 'Gs.'} {parseFloat(ventaExitosa.total).toLocaleString('es-PY')}
                            </p>
                        </div>

                        {ventaExitosa.aviso_facturacion && (
                            <div className="px-6 pb-4">
                                <div className="aviso text-left">
                                    <strong>La venta quedó registrada, pero sin factura.</strong><br />
                                    {ventaExitosa.aviso_facturacion} Emitila desde Historial de Ventas
                                    cuando se resuelva.
                                </div>
                            </div>
                        )}

                        {/* El comprobante, a la vista. El cajero ve lo que emitió sin
                            tener que pedirlo, e imprimir es un botón y no un paso
                            aparte. */}
                        <div className="px-6">
                            <VistaComprobante
                                venta={ventaExitosa}
                                documento={ventaExitosa.factura || null}
                                settings={settings}
                            />
                        </div>

                        <div className="px-6 py-4">
                            <button
                                onClick={() => setVentaExitosa(null)}
                                className="w-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-900 dark:text-white px-6 py-3 rounded-xl font-bold transition-all"
                            >
                                Nueva venta
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const PROCESADORAS = ['Visa', 'Mastercard', 'Bancard', 'Cabal', 'Amex'];
const ENTIDADES_QR = ['Tigo Money', 'Personal Pay', 'Claro Pay', 'PagoExpress', 'Otra'];

function DatosPagoPanel({ formaPago, datos, onChange }) {
    const set = (key, val) => onChange(prev => ({ ...prev, [key]: val }));

    const inputCls = 'w-full px-3 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/40 placeholder-slate-400 dark:placeholder-slate-600';
    const labelCls = 'block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1';

    return (
        <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl p-3 space-y-2.5">
            <p className="text-[10px] font-bold text-brand-800 uppercase tracking-wider">
                {formaPago === 'transferencia' && 'Datos de la Transferencia'}
                {formaPago === 'tarjeta' && 'Datos de la Tarjeta'}
                {formaPago === 'qr' && 'Datos del Pago QR'}
                <span className="text-slate-400 font-normal ml-1">(opcional)</span>
            </p>

            {formaPago === 'transferencia' && (<>
                <div>
                    <label htmlFor="carrito-banco-origen" className={labelCls}>Banco origen</label>
                    <input id="carrito-banco-origen" className={inputCls} placeholder="Ej: Itaú, BCP, Sudameris..."
                        value={datos.banco_origen || ''} onChange={e => set('banco_origen', e.target.value)} />
                </div>
                <div>
                    <label htmlFor="carrito-nro-cuenta-origen" className={labelCls}>Nro. cuenta origen</label>
                    <input id="carrito-nro-cuenta-origen" className={inputCls} placeholder="Ej: 001-123456-7"
                        value={datos.nro_cuenta || ''} onChange={e => set('nro_cuenta', e.target.value)} />
                </div>
                <div>
                    <label htmlFor="carrito-id-comprobante-de-transaccion" className={labelCls}>ID / Comprobante de transacción</label>
                    <input id="carrito-id-comprobante-de-transaccion" className={inputCls} placeholder="Ej: TRX202406240001"
                        value={datos.id_transaccion || ''} onChange={e => set('id_transaccion', e.target.value)} />
                </div>
            </>)}

            {formaPago === 'tarjeta' && (<>
                <div>
                    <label htmlFor="carrito-ultimos-4-digitos" className={labelCls}>Últimos 4 dígitos</label>
                    <input id="carrito-ultimos-4-digitos" className={inputCls} placeholder="1234" maxLength={4}
                        inputMode="numeric" pattern="[0-9]*"
                        value={datos.nro_tarjeta || ''} onChange={e => set('nro_tarjeta', e.target.value.replace(/\D/g, '').slice(0, 4))} />
                </div>
                <div>
                    <label htmlFor="carrito-banco-emisor" className={labelCls}>Banco emisor</label>
                    <input id="carrito-banco-emisor" className={inputCls} placeholder="Ej: BCP, Itaú, Vision..."
                        value={datos.banco || ''} onChange={e => set('banco', e.target.value)} />
                </div>
                <div>
                    <label htmlFor="carrito-procesadora-red" className={labelCls}>Procesadora / Red</label>
                    <select id="carrito-procesadora-red" className={inputCls} value={datos.procesadora || ''}
                        onChange={e => set('procesadora', e.target.value)}>
                        <option value="">Seleccionar...</option>
                        {PROCESADORAS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            </>)}

            {formaPago === 'qr' && (<>
                <div>
                    <label htmlFor="carrito-entidad-billetera" className={labelCls}>Entidad / Billetera</label>
                    <select id="carrito-entidad-billetera" className={inputCls} value={datos.entidad || ''}
                        onChange={e => set('entidad', e.target.value)}>
                        <option value="">Seleccionar...</option>
                        {ENTIDADES_QR.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="carrito-id-comprobante-de-transaccion-2" className={labelCls}>ID / Comprobante de transacción</label>
                    <input id="carrito-id-comprobante-de-transaccion-2" className={inputCls} placeholder="Ej: QR202406240001"
                        value={datos.id_transaccion || ''} onChange={e => set('id_transaccion', e.target.value)} />
                </div>
            </>)}
        </div>
    );
}
